"use client";

import Link from "next/link";
import { Search, Home, Handshake } from "lucide-react";
import { useBoundaryCopy } from "@/lib/locale/runtime-extra/use-boundary-copy";
import sourceEn from "@/lib/locale/runtime-extra/source.en.json";
import { defaultLocale } from "@/config/locale";
import { publicStorePath } from "@/lib/storefront/public-url";

/**
 * Global 404 page.
 *
 * Shown when a route doesn't exist.
 * Saleor channel (`standard`) is NOT in the URL — public URLs are
 * `/{locale}/...` (German has no prefix).
 */
export default function NotFound() {
	const { locale: extraLocale, copy, pending } = useBoundaryCopy();
	if (extraLocale && !copy && pending)
		return (
			<div aria-busy={pending} className="p-16 text-center">
				404
			</div>
		);
	// Without its loaded catalog a rollout locale keeps the default page and German links.
	const locale = copy ? extraLocale : null;
	const homeHref = publicStorePath(locale ?? defaultLocale, "/");
	const productsHref = publicStorePath(locale ?? defaultLocale, "/peptides");
	const partnersHref = publicStorePath(locale ?? defaultLocale, "/partners");
	const buttonBase =
		"inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

	return (
		<div className="flex min-h-[70vh] flex-col items-center justify-center px-4 py-16">
			<div className="mx-auto max-w-md text-center">
				{/* 404 Badge */}
				<span className="mb-4 inline-block rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
					404
				</span>

				{/* Heading */}
				<h1 className="mb-2 text-2xl font-bold tracking-tight text-foreground">
					{copy?.pageTitle ?? "Page Not Found"}
				</h1>

				{/* Message */}
				<p className="mb-8 text-muted-foreground">
					{copy?.["notFound.pageDescription"] ?? sourceEn["notFound.pageDescription"]}
				</p>

				{/* Actions */}
				<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
					<Link
						href={homeHref}
						className={`${buttonBase} hover:bg-primary/90 bg-primary text-primary-foreground`}
					>
						<Home className="h-4 w-4" />
						{copy?.home ?? "Go Home"}
					</Link>

					<Link
						href={productsHref}
						className={`${buttonBase} border border-input bg-background hover:bg-accent hover:text-accent-foreground`}
					>
						<Search className="h-4 w-4" />
						{copy?.products ?? "Browse Products"}
					</Link>

					<Link
						href={partnersHref}
						className={`${buttonBase} border border-input bg-background hover:bg-accent hover:text-accent-foreground`}
					>
						<Handshake className="h-4 w-4" />
						{copy?.partners ?? "Partners"}
					</Link>
				</div>
			</div>
		</div>
	);
}
